import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import apiFunctions from "../../../../services/api-service";
import { ArrowLeftIcon, ArrowDownTrayIcon, ListBulletIcon } from "@heroicons/react/24/outline";
import { EducationDAG } from "./EducationRuleCreate";

const EducationRuleTreeView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [rules, setRules] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    const dagContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get(`/educationrule/dropdownlist?page=1&limit=1000&search=`);
                setRules(response?.data?.data || []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const downloadTree = (format: "svg" | "png") => {
        const svg = dagContainerRef.current?.querySelector("svg");
        if (!svg) return;

        const clone = svg.cloneNode(true) as SVGSVGElement;
        clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
        const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        bg.setAttribute("width", "100%");
        bg.setAttribute("height", "100%");
        bg.setAttribute("fill", "#ffffff");
        clone.insertBefore(bg, clone.firstChild);
        const svgStr = new XMLSerializer().serializeToString(clone);

        if (format === "svg") {
            const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "education-rule-tree.svg";
            a.click();
            URL.revokeObjectURL(url);
            return;
        }

        const width = parseInt(svg.getAttribute("width") || "800");
        const height = parseInt(svg.getAttribute("height") || "600");
        const url = URL.createObjectURL(new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" }));
        const img = new Image();
        img.onload = () => {
            const scale = 2;
            const canvas = document.createElement("canvas");
            canvas.width = width * scale;
            canvas.height = height * scale;
            const ctx = canvas.getContext("2d");
            if (!ctx) return;
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0);
            URL.revokeObjectURL(url);
            canvas.toBlob((blob) => {
                if (!blob) return;
                const pngUrl = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = pngUrl;
                a.download = "education-rule-tree.png";
                a.click();
                URL.revokeObjectURL(pngUrl);
            }, "image/png");
        };
        img.src = url;
    };

    return (
        <>
            <div className="back-button" onClick={() => navigate("/admin/educationrule")}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <div className="page-title">
                <h3>Education Rule Dependency Tree</h3>
            </div>
            <div className="pb-4">
                <Card className="grid form-layout">
                    {loading ? (
                        <div className="flex justify-center items-center" style={{ minHeight: "300px" }}>
                            <ProgressSpinner />
                        </div>
                    ) : rules.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "64px 0" }}>
                            <ListBulletIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                            <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No education rules found</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-end gap-2 mb-3">
                                <Button
                                    type="button"
                                    label="PNG"
                                    icon={<ArrowDownTrayIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                                    outlined
                                    size="small"
                                    onClick={() => downloadTree("png")}
                                />
                                <Button
                                    type="button"
                                    label="SVG"
                                    icon={<ArrowDownTrayIcon style={{ width: "16px", height: "16px", marginRight: "6px" }} />}
                                    outlined
                                    size="small"
                                    onClick={() => downloadTree("svg")}
                                />
                            </div>
                            <div
                                ref={dagContainerRef}
                                style={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    overflowX: "auto",
                                    WebkitOverflowScrolling: "touch",
                                    background: "#f9fafb",
                                    borderRadius: "8px",
                                    border: "1px solid #e5e7eb",
                                    padding: "12px",
                                }}
                            >
                                <EducationDAG rules={rules} currentId={id} />
                            </div>
                            {id && (
                                <p className="text-xs text-gray-400 mt-2">
                                    <span style={{ display: "inline-block", width: 12, height: 12, background: "#eef2ff", border: "2px solid #4f46e5", borderRadius: 2, marginRight: 6, verticalAlign: "middle" }} />
                                    Selected rule
                                </p>
                            )}
                        </>
                    )}
                </Card>
            </div>
        </>
    );
};

export default EducationRuleTreeView;
